import React, { useState, useEffect } from "react";
import {
    Edit,
    Trash2,
    Plus,
    X,
    Save,
    ChevronDown,
    ChevronRight,
    Loader2,
    AlertCircle,
    CheckCircle,
    Image as ImageIcon
} from "lucide-react";
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { usePermissions } from '../hooks/usePermissions';


function PolicyEditor({ categoryId }) {
    const [productPolicies, setProductPolicies] = useState({
        returnPolicy: "",
        warrantyPolicy: "",
        shippingPolicy: "",
        cancellationPolicy: "",
    });
    const [refundPolicies, setRefundPolicies] = useState([]);
    const [success, setSuccess] = useState('');
    const { loading, error, makeRequest, clearError, handleFetchResponse } = useAuthenticatedApi();

    // State for adding/editing a specific refund policy
    const [editingRefundPolicy, setEditingRefundPolicy] = useState(null); // null or policy object for editing

    // Fetch all policies when the editor opens for a category
    useEffect(() => {
        const fetchPolicies = async () => {
            clearError();
            const response = await makeRequest(async (token) => {
                const fetchResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/categories/${categoryId}/policies`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                await handleFetchResponse(fetchResponse);
                return await fetchResponse.json();
            });

            if (response && response.success) {
                setProductPolicies(response.data.productPolicies || { returnPolicy: "", warrantyPolicy: "", shippingPolicy: "", cancellationPolicy: "" });
                setRefundPolicies(response.data.refundPolicies || []);
            }
        };

        if (categoryId) {
            fetchPolicies();
        }
    }, [categoryId, makeRequest, clearError, handleFetchResponse]);
    
    // Clear success message after a few seconds
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    // Handler to save product policies
    const handleSaveProductPolicies = async () => {
        clearError();
        const response = await makeRequest(async (token) => {
            const fetchResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/categories/${categoryId}/policies/product`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(productPolicies),
            });
            await handleFetchResponse(fetchResponse);
            return await fetchResponse.json();
        });

        if (response && response.success) {
            setSuccess('Product policies updated successfully!');
        }
    };

    // Handler to save (add or update) a refund policy
    const handleSaveRefundPolicy = async (e) => {
        e.preventDefault();
        clearError();
        const isNew = editingRefundPolicy._id === undefined;
        const url = isNew
            ? `${process.env.REACT_APP_API_BASE_URL}/categories/${categoryId}/policies/refund`
            : `${process.env.REACT_APP_API_BASE_URL}/categories/${categoryId}/policies/refund/${editingRefundPolicy._id}`;
        
        const method = isNew ? 'POST' : 'PUT';

        const response = await makeRequest(async (token) => {
            const fetchResponse = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(editingRefundPolicy),
            });
            await handleFetchResponse(fetchResponse);
            return await fetchResponse.json();
        });

        if (response && response.success) {
            if (isNew) {
                setRefundPolicies(prev => [...prev, response.data]);
            } else {
                setRefundPolicies(prev => prev.map(p => p._id === response.data._id ? response.data : p));
            }
            setEditingRefundPolicy(null); // Close the form
            setSuccess('Refund policy saved successfully!');
        }
    };
    
    // Handler to delete a refund policy
    const handleDeleteRefundPolicy = async (policyId) => {
        if (!window.confirm("Are you sure you want to delete this refund policy?")) return;
        clearError();
        
        const response = await makeRequest(async (token) => {
            const fetchResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/categories/${categoryId}/policies/refund/${policyId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            await handleFetchResponse(fetchResponse);
            return { ok: fetchResponse.ok };
        });

        if (response && response.ok) {
            setRefundPolicies(prev => prev.filter(p => p._id !== policyId));
            setSuccess('Refund policy deleted.');
        }
    };

    if (loading && !editingRefundPolicy) {
        return <div className="p-8 flex justify-center items-center"><Loader2 className="animate-spin text-blue-600" size={32} /> <span className="ml-2">Loading Policies...</span></div>
    }

    return (
        <div className="bg-gray-50 p-3 sm:p-6 space-y-6 sm:space-y-8">
            {error && <div className="p-3 my-2 bg-red-100 text-red-800 rounded border border-red-200 flex items-center gap-2"><AlertCircle size={16}/> {error}</div>}
            {success && <div className="p-3 my-2 bg-green-100 text-green-800 rounded border border-green-200 flex items-center gap-2"><CheckCircle size={16}/> {success}</div>}
            
            {/* Product Policies Section */}
            <div className="space-y-4 p-4 bg-white border rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Product Policies</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Return Policy</label>
                        <textarea value={productPolicies.returnPolicy} onChange={e => setProductPolicies(p => ({...p, returnPolicy: e.target.value}))} rows="3" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"></textarea>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Warranty Policy</label>
                        <textarea value={productPolicies.warrantyPolicy} onChange={e => setProductPolicies(p => ({...p, warrantyPolicy: e.target.value}))} rows="3" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"></textarea>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Policy</label>
                        <textarea value={productPolicies.shippingPolicy} onChange={e => setProductPolicies(p => ({...p, shippingPolicy: e.target.value}))} rows="3" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"></textarea>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Policy</label>
                        <textarea value={productPolicies.cancellationPolicy} onChange={e => setProductPolicies(p => ({...p, cancellationPolicy: e.target.value}))} rows="3" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"></textarea>
                    </div>
                </div>
                <div className="text-right pt-2">
                    <button onClick={handleSaveProductPolicies} disabled={loading} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2 ml-auto">
                        <Save size={16} /> {loading ? "Saving..." : "Save Product Policies"}
                    </button>
                </div>
            </div>

            {/* Refund Policies Section */}
            <div className="space-y-4 p-4 bg-white border rounded-lg">
                 <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 border-b pb-2">
                    <h3 className="text-lg font-semibold text-gray-900">Refund Policies</h3>
                    <button onClick={() => setEditingRefundPolicy({ type: 'full_refund', conditions: '', timeLimit: 30, refundPercentage: 100, isActive: true })} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center justify-center gap-1 w-full sm:w-auto">
                        <Plus size={16}/> Add Rule
                    </button>
                </div>

                {/* Form for Adding/Editing Refund Policy */}
                {editingRefundPolicy && (
                    <form onSubmit={handleSaveRefundPolicy} className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 border-dashed space-y-4">
                        <h4 className="font-semibold text-lg text-gray-800">{editingRefundPolicy._id ? "Edit" : "Add New"} Refund Rule</h4>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                <select value={editingRefundPolicy.type} onChange={e => setEditingRefundPolicy(p => ({ ...p, type: e.target.value, refundPercentage: e.target.value === 'partial_refund' ? p.refundPercentage : 100 }))} className="w-full p-2 border border-gray-300 rounded-md bg-white">
                                    <option value="full_refund">Full Refund</option>
                                    <option value="partial_refund">Partial Refund</option>
                                    <option value="no_refund">No Refund</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Time Limit (in Days)</label>
                                <input type="number" value={editingRefundPolicy.timeLimit} onChange={e => setEditingRefundPolicy(p => ({ ...p, timeLimit: Number(e.target.value) }))} className="w-full p-2 border border-gray-300 rounded-md" />
                            </div>
                             {editingRefundPolicy.type === 'partial_refund' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Refund Percentage (%)</label>
                                    <input type="number" min="0" max="100" value={editingRefundPolicy.refundPercentage} onChange={e => setEditingRefundPolicy(p => ({ ...p, refundPercentage: Number(e.target.value) }))} className="w-full p-2 border border-gray-300 rounded-md" />
                                </div>
                            )}
                            <div className="flex items-center gap-2 pt-6">
                                <input type="checkbox" checked={editingRefundPolicy.isActive} onChange={e => setEditingRefundPolicy(p => ({...p, isActive: e.target.checked}))} id={`isActiveCheck-${categoryId}`} className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"/>
                                <label htmlFor={`isActiveCheck-${categoryId}`} className="text-sm font-medium text-gray-700">Is Active</label>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Conditions</label>
                            <textarea value={editingRefundPolicy.conditions} onChange={e => setEditingRefundPolicy(p => ({ ...p, conditions: e.target.value }))} rows="3" className="w-full p-2 border border-gray-300 rounded-md"></textarea>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button type="button" onClick={() => setEditingRefundPolicy(null)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 flex items-center gap-2"><X size={16}/>Cancel</button>
                            <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2">
                                <Save size={16}/> {loading ? "Saving..." : "Save Rule"}
                            </button>
                        </div>
                    </form>
                )}

                {/* List of Existing Refund Policies */}
                <div className="space-y-3 pt-2">
                    {refundPolicies.length > 0 ? refundPolicies.map(policy => (
                        <div key={policy._id} className="p-3 bg-white border-l-4 rounded-r-lg flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3" style={{ borderLeftColor: policy.isActive ? '#10B981' : '#EF4444' }}>
                            <div>
                                <p className="font-semibold capitalize text-gray-800">{policy.type.replace('_', ' ')} {policy.type === 'partial_refund' ? `(${policy.refundPercentage}%)` : ''}</p>
                                <p className="text-sm text-gray-600 mt-1">{policy.conditions}</p>
                                <div className="text-xs text-gray-500 mt-2">
                                    <span>Time Limit: {policy.timeLimit} days</span>
                                </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0 ml-4">
                                <button onClick={() => setEditingRefundPolicy(policy)} title="Edit Policy" className="p-2 text-blue-600 hover:bg-blue-100 rounded-full"><Edit size={16} /></button>
                                <button onClick={() => handleDeleteRefundPolicy(policy._id)} title="Delete Policy" className="p-2 text-red-600 hover:bg-red-100 rounded-full"><Trash2 size={16} /></button>
                            </div>
                        </div>
                    )) : <p className="text-center text-gray-500 py-4">No refund rules have been added for this category.</p>}
                </div>
            </div>
        </div>
    );
}


export default function CategoryPoliciesPanel() {
    const permissions = usePermissions('Product Policies');
    const [categories, setCategories] = useState([]);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const { loading, error, makeRequest, clearError, handleFetchResponse } = useAuthenticatedApi();

    // Fetch categories on component mount
    useEffect(() => {
        const fetchCategories = async () => {
            clearError();
            const response = await makeRequest(async (token) => {
                const fetchResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/categories/zeroLevelCategories`, {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        "ngrok-skip-browser-warning": "true",
                    },
                });
                await handleFetchResponse(fetchResponse);
                return await fetchResponse.json();
            });

            if (response && response.success) {
                setCategories(response.data);
            }
        };
        
        fetchCategories();
    }, [makeRequest, clearError, handleFetchResponse]);

    const toggleEditor = (categoryId) => {
        setEditingCategoryId(prevId => (prevId === categoryId ? null : categoryId));
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
                <div className="mb-6 sm:mb-8">
                    <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Product Policy Management</h1>
                    <p className="text-gray-600 mt-1">Manage product and refund policies for top-level categories.</p>
                </div>
                
                {error && (
                    <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 flex items-center justify-between">
                        <div className="flex items-center gap-3"><AlertCircle className="text-red-600" size={20} /><span className="text-red-800 font-medium">{error}</span></div>
                        <button onClick={() => clearError()} className="text-red-600 hover:bg-red-100 p-1 rounded"><X size={16} /></button>
                    </div>
                )}
                
                <div className="bg-white rounded-xl shadow-lg border border-gray-200">
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                            <div className="text-blue-600 font-medium">Loading Categories...</div>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {categories.length === 0 && !error ? (
                                <div className="text-center py-12 text-gray-500">No top-level categories found.</div>
                            ) : (
                                categories.map(category => {
                                    const isEditing = editingCategoryId === category._id;
                                    return (
                                        <div key={category._id}>
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-gray-50">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    {category.imageUrl ? (
                                                        <img src={category.imageUrl} alt={category.name} className="h-10 w-10 sm:h-12 sm:w-12 object-cover rounded-lg border flex-shrink-0"/>
                                                    ) : (
                                                        <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0"><ImageIcon size={20} className="text-gray-400" /></div>
                                                    )}
                                                    <span className="font-semibold text-gray-900 text-base sm:text-lg truncate">{category.name}</span>
                                                </div>
                                                <button
                                                    onClick={() => toggleEditor(category._id)}
                                                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors w-full sm:w-auto flex-shrink-0 ${isEditing ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                                                >
                                                    {isEditing ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                    {isEditing ? 'Hide Policies' : 'Manage Policies'}
                                                </button>
                                            </div>
                                            {isEditing && (
                                                <PolicyEditor categoryId={category._id} />
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}